import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Phone, ArrowRight, Star, Shield, Zap, CheckCircle, Heart, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getPageUrl } from '../utils/slugMap';

const API = process.env.REACT_APP_BACKEND_URL;

const iconMap = { star: Star, shield: Shield, zap: Zap, check: CheckCircle, heart: Heart, award: Award };

const HeroBlock = ({ content }) => (
  <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 to-slate-800 overflow-hidden" style={content.backgroundImage ? { backgroundImage: `url(${content.backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
    {content.backgroundImage && content.overlay && <div className="absolute inset-0 bg-slate-900/70" />}
    <div className="relative max-w-6xl mx-auto px-6 text-center">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">{content.heading}</h1>
        {content.subtext && <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">{content.subtext}</p>}
        {content.buttonText && (
          <Link to={content.buttonLink || '/contact'}>
            <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8">
              {content.buttonText} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        )}
      </motion.div>
    </div>
  </section>
);

const RenderBody = ({ body }) => {
  if (!body) return null;
  // If content contains HTML tags, render as HTML
  if (body.includes('<')) {
    return <div className="prose-cms" dangerouslySetInnerHTML={{ __html: body }} />;
  }
  // Legacy plain text — split by newlines
  return body.split('\n').map((p, i) => (
    <p key={i} className="text-slate-600 leading-relaxed mb-4">{p}</p>
  ));
};

const TextBlock = ({ content }) => (
  <section className="py-16 bg-white">
    <div className="max-w-4xl mx-auto px-6" style={{ textAlign: content.alignment || 'left' }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {content.heading && <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">{content.heading}</h2>}
        <RenderBody body={content.body} />
      </motion.div>
    </div>
  </section>
);

const ImageTextBlock = ({ content }) => {
  const hasImage = content.image && content.image.trim();
  return (
    <section className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className={hasImage ? 'grid lg:grid-cols-2 gap-12 items-center' : ''}>
            <div className={hasImage && content.imagePosition === 'left' ? 'lg:order-2' : ''}>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">{content.heading}</h2>
              <RenderBody body={content.body} />
              {content.buttonText && (
                <Link to={content.buttonLink || '/contact'}>
                  <Button className="bg-sky-500 hover:bg-sky-600 text-white mt-2">
                    {content.buttonText} <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              )}
            </div>
            {hasImage && (
              <div className={content.imagePosition === 'left' ? 'lg:order-1' : ''}>
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img src={content.image} alt={content.heading} loading="lazy" decoding="async" width={1200} height={800} className="w-full h-[350px] object-cover" />
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const FeaturesBlock = ({ content }) => (
  <section className="py-16 bg-slate-50">
    <div className="max-w-6xl mx-auto px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">{content.heading}</h2>
          {content.subtext && <p className="text-slate-600">{content.subtext}</p>}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(content.items || []).map((item, i) => {
            const Icon = iconMap[item.icon] || Star;
            return (
              <Card key={item.title} className="border-slate-200 hover:border-sky-500 transition-colors hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="p-3 bg-sky-50 rounded-xl w-fit mb-4"><Icon className="h-6 w-6 text-sky-600" /></div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </motion.div>
    </div>
  </section>
);

const CTABlock = ({ content }) => (
  <section className="pt-16 pb-[100px]" style={{ backgroundColor: content.backgroundColor || '#0ea5e9' }}>
    <div className="max-w-4xl mx-auto px-6 text-center">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{content.heading}</h2>
        {content.subtext && <p className="text-white/80 mb-8 text-lg">{content.subtext}</p>}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {content.buttonText && (
            <Link to={content.buttonLink || '/contact'}>
              <Button size="lg" className="bg-white text-sky-700 hover:bg-slate-100 font-semibold px-8">
                {content.buttonText}
              </Button>
            </Link>
          )}
          <a href="tel:813-453-5988">
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold px-8">
              <Phone className="h-4 w-4 mr-2" /> 813-453-5988
            </Button>
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

const GalleryBlock = ({ content }) => {
  const visibleImages = useMemo(() => 
    (content.images || []).filter(img => img.url), 
    [content.images]
  );

  return (
    <section className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {content.heading && <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center">{content.heading}</h2>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleImages.map((img) => (
              <div key={img.url} className="rounded-xl overflow-hidden shadow-lg group">
                <img src={img.url} alt={img.caption || ''} loading="lazy" decoding="async" width={1200} height={800} className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300" />
                {img.caption && <p className="p-3 text-sm text-slate-600 bg-white">{img.caption}</p>}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const blockRenderers = {
  hero: HeroBlock,
  text: TextBlock,
  image_text: ImageTextBlock,
  features: FeaturesBlock,
  cta: CTABlock,
  gallery: GalleryBlock,
};

export const CMSPage = ({ slug: propSlug }) => {
  const { slug: paramSlug } = useParams();
  const slug = propSlug || paramSlug;
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await fetch(`${API}/api/pages/${slug}`);
        if (res.ok) {
          setPage(await res.json());
        } else {
          setError(true);
        }
      } catch { setError(true); }
      setLoading(false);
    };
    fetchPage();
  }, [slug]);

  // Set document title directly as react-helmet-async has timing issues with async data
  useEffect(() => {
    if (page?.title) {
      document.title = `${page.title} | A-Quality Pool Service`;
    }
    return () => {
      document.title = 'A-Quality Pool Service | Florida\'s Premier Pool Service';
    };
  }, [page?.title]);

  const sortedBlocks = useMemo(() => 
    page?.blocks ? [...page.blocks].sort((a, b) => (a.order || 0) - (b.order || 0)) : [],
    [page?.blocks]
  );

  if (loading) return <div className="pt-32 pb-16 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500 mx-auto" /></div>;
  if (error || !page) return (
    <div className="pt-32 pb-16 text-center">
      <h1 className="text-3xl font-bold text-slate-900">Page Not Found</h1>
      <Link to="/" className="text-sky-500 mt-4 inline-block">Go Home</Link>
    </div>
  );

  return (
    <div data-testid={`cms-page-${page.slug}`}>
      <Helmet>
        <title>{page.title} | A-Quality Pool Service</title>
        {page.description && <meta name="description" content={page.description} />}
        <link rel="canonical" href={`https://a-qualitypools.net${getPageUrl(page.slug)}`} />
      </Helmet>
      {sortedBlocks.map((block) => {
        const Renderer = blockRenderers[block.type];
        return Renderer ? <Renderer key={block.id} content={block.content} /> : null;
      })}
    </div>
  );
};

export default CMSPage;
